import React, { useState, useEffect, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AssetContext } from '../contexts/AssetListContext.js';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend } from 'recharts';
import backIcon from '../assets/icons/navigate_before.svg';
import '../css/Graphics.css';

const Graphics = () => {
    const { defects } = useContext(AssetContext);
    const navigate = useNavigate();
    const [colors, setColors] = useState({});
    const [viewMode, setViewMode] = useState('risk-rating'); // 'risk-rating' or 'severity-level'
    
    // Filter states for horizontal bar charts
    const [assetFilters, setAssetFilters] = useState([true, true, true, true]);
    const [defectTypeFilters, setDefectTypeFilters] = useState([true, true, true, true]);
    
    // Custom tooltip state
    const [tooltip, setTooltip] = useState({ show: false, content: '', x: 0, y: 0 });

    // Tooltip handlers
    const showTooltip = (e, content) => {
        const rect = e.target.getBoundingClientRect();
        setTooltip({
            show: true,
            content,
            x: rect.left + rect.width / 2,
            y: rect.top - 10
        });
    };

    const hideTooltip = () => {
        setTooltip({ ...tooltip, show: false });
    };

    // Configuration for each view mode
    const viewConfig = {
        'risk-rating': {
            labels: ['Very High', 'High', 'Medium', 'Low'],
            sublabels: ['Score 23-25', 'Score 17-22', 'Score 7-16', 'Score 1-6'],
            field: 'risk_rate',
            donutTitle: 'Defects by Risk Rating',
            barTitle: 'Defects by Risk Rating and Task Type',
            assetTitle: 'Defects by Asset and Risk Rating',
            defectTypeTitle: 'Defects by Defect Type and Risk Rating',
            statusTitle: 'Open vs Completed Defects by Risk Rating',
            printLabel: 'Risk Rating'
        },
        'severity-level': {
            labels: ['Critical', 'Abnormal', 'Moderate', 'Normal'],
            sublabels: ['Score 20-25', 'Score 11-19', 'Score 4-10', 'Score 1-3'],
            field: 'severity_level',
            donutTitle: 'Defects by Severity Level',
            barTitle: 'Defects by Severity Level and Task Type',
            assetTitle: 'Defects by Asset and Severity Level',
            defectTypeTitle: 'Defects by Defect Type and Severity Level',
            statusTitle: 'Open vs Completed Defects by Severity Level',
            printLabel: 'Severity Level'
        }
    };

    const currentConfig = viewConfig[viewMode];

    // Get CSS variable colors when component mounts
    useEffect(() => {
        const riskColors = {
            'Very High': getCssVariableValue('--risk-color-4'),
            'High': getCssVariableValue('--risk-color-3'),
            'Medium': getCssVariableValue('--risk-color-2'),
            'Low': getCssVariableValue('--risk-color-1'),
        };
        setColors(riskColors);
    }, []);

    const getCssVariableValue = (variableName) => {
        return getComputedStyle(document.documentElement).getPropertyValue(variableName).trim();
    };

    // Category colors using CSS variables
    const categoryColors = [
        colors['Very High'] || '#FF0000',  // Very High / Critical
        colors['High'] || '#FFC000',       // High / Abnormal
        colors['Medium'] || '#FFFF00',     // Medium / Moderate
        colors['Low'] || '#00B050'         // Low / Normal
    ];

    const goBack = () => {
        navigate('/analytics');
    };

    // Reset filters when switching view mode
    const handleViewModeChange = (mode) => {
        setViewMode(mode);
        setAssetFilters([true, true, true, true]);
        setDefectTypeFilters([true, true, true, true]);
    };

    // Calculate KPI counts
    const getKpiCounts = () => {
        const counts = currentConfig.labels.map(label => 
            defects.filter(d => d[currentConfig.field] === label).length
        );
        return counts;
    };

    // Calculate donut chart data
    const getDonutData = () => {
        return currentConfig.labels.map((label, idx) => ({
            name: label,
            value: defects.filter(d => d[currentConfig.field] === label).length,
            color: categoryColors[idx]
        }));
    };

    // Calculate bar chart data (by Task Type)
    const getTaskTypeData = () => {
        return currentConfig.labels.map((label, idx) => {
            const filtered = defects.filter(d => d[currentConfig.field] === label);
            return {
                name: label,
                Corrective: filtered.filter(d => d['Task type'] === 'Corrective').length,
                Preventive: filtered.filter(d => d['Task type'] === 'Preventive').length,
                color: categoryColors[idx]
            };
        });
    };

    // Calculate horizontal bar data by asset
    const getAssetData = () => {
        const assetGroups = {};
        defects.forEach(defect => {
            const assetName = defect.asset_name;
            const category = defect[currentConfig.field];
            const categoryIdx = currentConfig.labels.indexOf(category);
            
            if (!assetGroups[assetName]) {
                assetGroups[assetName] = [0, 0, 0, 0];
            }
            if (categoryIdx !== -1) {
                assetGroups[assetName][categoryIdx]++;
            }
        });

        return Object.entries(assetGroups)
            .map(([name, values]) => ({ name, values }))
            .sort((a, b) => {
                const totalA = a.values.reduce((sum, v, idx) => sum + (assetFilters[idx] ? v : 0), 0);
                const totalB = b.values.reduce((sum, v, idx) => sum + (assetFilters[idx] ? v : 0), 0);
                return totalB - totalA;
            });
    };

    // Calculate horizontal bar data by defect type
    const getDefectTypeData = () => {
        const defectTypeGroups = {};
        defects.forEach(defect => {
            const defectType = defect.defect_type;
            const category = defect[currentConfig.field];
            const categoryIdx = currentConfig.labels.indexOf(category);
            
            if (!defectTypeGroups[defectType]) {
                defectTypeGroups[defectType] = [0, 0, 0, 0];
            }
            if (categoryIdx !== -1) {
                defectTypeGroups[defectType][categoryIdx]++;
            }
        });

        return Object.entries(defectTypeGroups)
            .map(([name, values]) => ({ name, values }))
            .sort((a, b) => {
                const totalA = a.values.reduce((sum, v, idx) => sum + (defectTypeFilters[idx] ? v : 0), 0);
                const totalB = b.values.reduce((sum, v, idx) => sum + (defectTypeFilters[idx] ? v : 0), 0);
                return totalB - totalA;
            });
    };

    // Calculate open vs completed data
    const getStatusData = () => {
        const openData = currentConfig.labels.map(label => 
            defects.filter(d => d[currentConfig.field] === label && d['Job Status'] !== 'Work Completed').length
        );
        const completedData = currentConfig.labels.map(label => 
            defects.filter(d => d[currentConfig.field] === label && d['Job Status'] === 'Work Completed').length
        );
        return { openData, completedData };
    };

    const kpiCounts = getKpiCounts();
    const donutData = getDonutData();
    const taskTypeData = getTaskTypeData();
    const assetData = getAssetData();
    const defectTypeData = getDefectTypeData();
    const statusData = getStatusData();
    const totalDefects = defects.length;

    // Calculate max value for horizontal bars
    const getMaxBarValue = (data, filters) => {
        const totals = data.map(item => 
            item.values.reduce((sum, v, idx) => sum + (filters[idx] ? v : 0), 0)
        );
        return Math.max(...totals, 1);
    };

    // Render horizontal bar row
    const renderHorizontalBar = (item, filters, maxValue) => {
        const filteredTotal = item.values.reduce((sum, v, idx) => sum + (filters[idx] ? v : 0), 0);
        
        return (
            <div className="hbar-row" key={item.name}>
                <div className="hbar-label" title={item.name}>{item.name}</div>
                <div className="hbar-track">
                    {item.values.map((val, idx) => {
                        if (val === 0 || !filters[idx]) return null;
                        const width = (val / maxValue) * 100;
                        return (
                            <div 
                                key={idx}
                                className={`hbar-segment cat-${idx + 1}`}
                                style={{ width: `${width}%`, cursor: 'pointer' }}
                                onMouseEnter={(e) => showTooltip(e, `${currentConfig.labels[idx]}: ${val}`)}
                                onMouseLeave={hideTooltip}
                            >
                                {val > 0 && val}
                            </div>
                        );
                    })}
                </div>
                <div className="hbar-total">{filteredTotal}</div>
            </div>
        );
    };

    // Render status bars
    const renderStatusBars = (data, labels) => {
        const maxValue = Math.max(...data, 1);
        return labels.map((label, idx) => (
            <div className="status-bar-row" key={label}>
                <span className="status-bar-label">{label}</span>
                <div 
                    className="status-bar-track"
                    style={{ cursor: 'pointer' }}
                    onMouseEnter={(e) => showTooltip(e, `${label}: ${data[idx]}`)}
                    onMouseLeave={hideTooltip}
                >
                    <div 
                        className={`status-bar-fill cat-${idx + 1}`}
                        style={{ width: `${(data[idx] / maxValue) * 100}%` }}
                    />
                </div>
                <span className="status-bar-value">{data[idx]}</span>
            </div>
        ));
    };

    // Custom tooltip for bar chart
    const CustomBarTooltip = ({ active, payload, label }) => {
        if (!active || !payload) return null;
        return (
            <div className="custom-tooltip">
                <p className="tooltip-label">{label}</p>
                {payload.map((item, idx) => (
                    <p key={idx} style={{ color: item.color }}>
                        {item.name}: {item.value}
                    </p>
                ))}
            </div>
        );
    };

    // Export PDF function
    const exportPDF = () => {
        window.print();
    };

    return (
        <div className="defect-summary-container">
            {/* Navigation Bar */}
            <div className="cost-analytics-title">
                <div>
                    <h2>Analytics</h2>
                    <Link to="/cost-analysis" className="analysis-button">
                        Cost Analysis
                    </Link>
                    <Link to="/remedial-actions" className="analysis-button">
                        Remedial Actions
                    </Link>
                    <Link to="/graphics" className="analysis-button-active">
                        Defect Summary
                    </Link>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    {/* Toggle Switch */}
                    <div className="view-toggle-container">
                        <span className="view-toggle-label" data-current-view={currentConfig.printLabel}>View by:</span>
                        <div className="view-toggle-switch"><button 
                                className={`view-toggle-option ${viewMode === 'risk-rating' ? 'active' : ''}`}
                                onClick={() => handleViewModeChange('risk-rating')}
                            >Risk Rating</button><button 
                                className={`view-toggle-option ${viewMode === 'severity-level' ? 'active' : ''}`}
                                onClick={() => handleViewModeChange('severity-level')}
                            >Severity Level</button></div>
                    </div>

                    {/* Legend */}
                    <div className="summary-legend">
                        {currentConfig.labels.map((label, idx) => (
                            <div className="summary-legend-item" key={label}>
                                <div className={`summary-legend-dot cat-${idx + 1}`}></div>
                                <span>{label}</span>
                            </div>
                        ))}
                    </div>

                    <button className="download-excel-button" onClick={exportPDF}>
                        📄 Export PDF
                    </button>

                    <button className="back-button" style={{ width: '100px' }} onClick={goBack}>
                        <img src={backIcon} alt="Back" />
                        Analytics
                    </button>
                </div>
            </div>

            {/* Main Content */}
            <main className="summary-main-content">
                {/* KPI Cards Row */}
                <div className="kpi-row">
                    {currentConfig.labels.map((label, idx) => (
                        <div className={`kpi-card cat-${idx + 1}`} key={label}>
                            <div className="kpi-value">{kpiCounts[idx]}</div>
                            <div className="kpi-label">
                                {label} {viewMode === 'risk-rating' ? 'Risk' : ''}
                            </div>
                            <div className="kpi-sublabel">{currentConfig.sublabels[idx]}</div>
                        </div>
                    ))}
                </div>

                {/* Donut Chart */}
                <div className="summary-card">
                    <h3 className="summary-card-title">{currentConfig.donutTitle}</h3>
                    <div className="donut-container">
                        <div className="donut-chart-wrapper">
                            <ResponsiveContainer width="100%" height={260}>
                                <PieChart>
                                    <Pie
                                        data={donutData}
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={70}
                                        outerRadius={110}
                                        dataKey="value"
                                    >
                                        {donutData.map((entry, idx) => (
                                            <Cell key={idx} fill={categoryColors[idx]} />
                                        ))}
                                    </Pie>
                                    <Tooltip formatter={(value, name) => [value, name]} />
                                </PieChart>
                            </ResponsiveContainer>
                            <div className="donut-center-text">
                                <div className="donut-total">{totalDefects}</div>
                                <div className="donut-label">Total Defects</div>
                            </div>
                        </div>
                        <div className="chart-legend">
                            {donutData.map((item, idx) => (
                                <div className="chart-legend-item" key={item.name}>
                                    <div className="chart-legend-box" style={{ background: categoryColors[idx] }}></div>
                                    <span>{item.name} ({item.value})</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Bar Chart - Task Type - Using Blue and Grey colors */}
                <div className="summary-card">
                    <h3 className="summary-card-title">{currentConfig.barTitle}</h3>
                    <div className="bar-chart-container">
                        <ResponsiveContainer width="100%" height={300}>
                            <BarChart data={taskTypeData}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="name" tick={{ fontSize: 12, fontFamily: 'Arboria Book' }} />
                                <YAxis tick={{ fontSize: 12, fontFamily: 'Arboria Book' }} />
                                <Tooltip content={<CustomBarTooltip />} />
                                <Legend wrapperStyle={{ fontFamily: 'Arboria Book', fontSize: 12 }} />
                                <Bar dataKey="Corrective" name="Corrective (C)" fill="#5B9BD5" />
                                <Bar dataKey="Preventive" name="Preventive (P)" fill="#A6A6A6" />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                    <div className="chart-filter-row">
                        <span style={{ fontSize: '12px', color: '#767676', fontFamily: 'Arboria Book' }}>C = Corrective &nbsp; | &nbsp; P = Preventive</span>
                    </div>
                </div>

                {/* Horizontal Bar - By Asset */}
                <div className="summary-card">
                    <h3 className="summary-card-title">{currentConfig.assetTitle}</h3>
                    <div className="hbar-container">
                        {assetData.map(item => renderHorizontalBar(item, assetFilters, getMaxBarValue(assetData, assetFilters)))}
                    </div>
                    <div className="chart-filter-row">
                        {currentConfig.labels.map((label, idx) => (
                            <label className="chart-filter-item" key={label}>
                                <input 
                                    type="checkbox" 
                                    checked={assetFilters[idx]}
                                    onChange={() => {
                                        const newFilters = [...assetFilters];
                                        newFilters[idx] = !newFilters[idx];
                                        setAssetFilters(newFilters);
                                    }}
                                />
                                <span>{label}</span>
                            </label>
                        ))}
                    </div>
                </div>

                {/* Horizontal Bar - By Defect Type */}
                <div className="summary-card">
                    <h3 className="summary-card-title">{currentConfig.defectTypeTitle}</h3>
                    <div className="hbar-container">
                        {defectTypeData.map(item => renderHorizontalBar(item, defectTypeFilters, getMaxBarValue(defectTypeData, defectTypeFilters)))}
                    </div>
                    <div className="chart-filter-row">
                        {currentConfig.labels.map((label, idx) => (
                            <label className="chart-filter-item" key={label}>
                                <input 
                                    type="checkbox" 
                                    checked={defectTypeFilters[idx]}
                                    onChange={() => {
                                        const newFilters = [...defectTypeFilters];
                                        newFilters[idx] = !newFilters[idx];
                                        setDefectTypeFilters(newFilters);
                                    }}
                                />
                                <span>{label}</span>
                            </label>
                        ))}
                    </div>
                </div>

                {/* Open vs Completed */}
                <div className="summary-card full-width">
                    <h3 className="summary-card-title">{currentConfig.statusTitle}</h3>
                    <div className="status-grid">
                        <div className="status-section">
                            <div className="status-header">
                                <span className="status-title">Open Defects</span>
                                <span className="status-badge open">
                                    {statusData.openData.reduce((a, b) => a + b, 0)} Total
                                </span>
                            </div>
                            <div className="status-bars">
                                {renderStatusBars(statusData.openData, currentConfig.labels)}
                            </div>
                        </div>
                        <div className="status-section">
                            <div className="status-header">
                                <span className="status-title">Completed Defects</span>
                                <span className="status-badge completed">
                                    {statusData.completedData.reduce((a, b) => a + b, 0)} Total
                                </span>
                            </div>
                            <div className="status-bars">
                                {renderStatusBars(statusData.completedData, currentConfig.labels)}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Reference Boxes */}
                <div className="summary-card full-width">
                    <div className="reference-container">
                        {/* Risk Rating Reference */}
                        <div className="reference-box">
                            <div className="reference-title">Risk Rating Reference (GCAA Risk Matrix)</div>
                            <div className="reference-scale">
                                <div className="reference-item cat-1">
                                    <div className="color-bar"></div>
                                    <div className="content">
                                        <div className="reference-item-title">Very High Risk</div>
                                        <div className="reference-item-desc">Must be signed-off by Risk Owner, communicated and escalated to CEO, CFO, Head of Industrial Assets and General Counsel.</div>
                                        <div className="reference-item-score">Risk Score: 23-25</div>
                                    </div>
                                </div>
                                <div className="reference-item cat-2">
                                    <div className="color-bar"></div>
                                    <div className="content">
                                        <div className="reference-item-title">High Risk</div>
                                        <div className="reference-item-desc">Must be signed-off by Risk Owner, communicated and escalated to Industrial Lead, Group Functional Leads and Head of Industrial Assets.</div>
                                        <div className="reference-item-score">Risk Score: 17-22</div>
                                    </div>
                                </div>
                                <div className="reference-item cat-3">
                                    <div className="color-bar"></div>
                                    <div className="content">
                                        <div className="reference-item-title">Medium Risk</div>
                                        <div className="reference-item-desc">Implemented based on Return on Investment and/or agreement of Risk Owner, supported by consensus of risk assessment team.</div>
                                        <div className="reference-item-score">Risk Score: 7-16</div>
                                    </div>
                                </div>
                                <div className="reference-item cat-4">
                                    <div className="color-bar"></div>
                                    <div className="content">
                                        <div className="reference-item-title">Low Risk</div>
                                        <div className="reference-item-desc">Additional Risk Treatment should only be implemented if simple and inexpensive.</div>
                                        <div className="reference-item-score">Risk Score: 1-6</div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Severity Level Reference */}
                        <div className="reference-box">
                            <div className="reference-title">Severity Level Reference (Fixed Structures Guideline)</div>
                            <div className="reference-scale">
                                <div className="reference-item cat-1">
                                    <div className="color-bar"></div>
                                    <div className="content">
                                        <div className="reference-item-title">Critical</div>
                                        <div className="reference-item-desc">Exceeded maximum tolerable limits. Failure probable within normal inspection frequency. Corrective action execution immediately.</div>
                                        <div className="reference-item-score">Risk Score: 20-25</div>
                                    </div>
                                </div>
                                <div className="reference-item cat-2">
                                    <div className="color-bar"></div>
                                    <div className="content">
                                        <div className="reference-item-title">Abnormal</div>
                                        <div className="reference-item-desc">Outside acceptable limits, but structure maintains adequate integrity. Corrective action planning should be initiated.</div>
                                        <div className="reference-item-score">Risk Score: 11-19</div>
                                    </div>
                                </div>
                                <div className="reference-item cat-3">
                                    <div className="color-bar"></div>
                                    <div className="content">
                                        <div className="reference-item-title">Moderate</div>
                                        <div className="reference-item-desc">Signs of deterioration from "as new" state but not severe enough to impact integrity. Preventive actions should be considered.</div>
                                        <div className="reference-item-score">Risk Score: 4-10</div>
                                    </div>
                                </div>
                                <div className="reference-item cat-4">
                                    <div className="color-bar"></div>
                                    <div className="content">
                                        <div className="reference-item-title">Normal</div>
                                        <div className="reference-item-desc">Within acceptable limits. No signs of excessive corrosion, wear, damage or deflection. No action required between normal inspection frequencies.</div>
                                        <div className="reference-item-score">Risk Score: 1-3</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </main>
            
            {/* Custom Styled Tooltip */}
            {tooltip.show && (
                <div 
                    className="styled-tooltip"
                    style={{
                        position: 'fixed',
                        left: tooltip.x,
                        top: tooltip.y,
                        transform: 'translate(-50%, -100%)',
                    }}
                >
                    {tooltip.content}
                </div>
            )}
        </div>
    );
};

export default Graphics;
